import { LoginResponse, Menus, MenuType } from './dto/response';
import { DownloadFiles } from './services/project/project';
import { showSnackbar } from '../redux/reducer/snackbarSlice';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { openDrawer } from '../redux/reducer/drawerSlice';

const CryptoJS = require('crypto-js');

export function encryptText(plainText: any) {
    if (!plainText) return ''
    let key = CryptoJS.enc.Utf8.parse('22fd580b89e9c1db824253b34d8476fec9ec29d4dce249b980d0e7d34dce5b0c');
    let iv = CryptoJS.enc.Utf8.parse('9c2b9115327c834e3fa023377ad3f999');
    let config = { keySize: 128, iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 };
    let encPassword = CryptoJS.AES.encrypt(plainText, key, config);
    return encPassword.ciphertext.toString(CryptoJS.enc.Base64);
}

export const formatUserType = (userType?: string) => {
    if (!userType) return "";
    return userType
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};


export const decryptText = (text: any) => {
    if (!text) return ''
    let key = CryptoJS.enc.Utf8.parse('22fd580b89e9c1db824253b34d8476fec9ec29d4dce249b980d0e7d34dce5b0c');
    let iv = CryptoJS.enc.Utf8.parse('9c2b9115327c834e3fa023377ad3f999');
    let config = { keySize: 128, iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 };
    try {
        let encPassword = CryptoJS.AES.decrypt(text, key, config);
        return encPassword.toString(CryptoJS.enc.Utf8);
    } catch (err) {
        try {
            return JSON.parse(text)
        } catch (err1) {
            return text || ''
        }
    }
}

export const isObject = (obj: any) => typeof obj === 'object' && !Array.isArray(obj) && obj !== null

export const stableSort = <T>(array: readonly T[], comparator: (a: T, b: T) => number) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export const getComparator = <Key extends keyof any>(order: any, orderBy: Key,): (
    a: { [key in Key]: any },
    b: { [key in Key]: any },
) => number => {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

export const descendingComparator = <T>(a: T, b: T, orderBy: keyof T) => {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

export const isLoggedIn = (state: LoginResponse) => {
    const { token, userDetails, session_expires } = state;
    return !!userDetails && !!token && !!session_expires;
}


export const isAuthorizedPage = (moduleUrls: string) => {
    return true
}


export const rupeeAmountDisplay = (amount: number) => {
    if (!amount) {
        return `₹ 0`
    }
    return `₹ ${AmountDisplay(amount)}`;
};

export const AmountDisplay = (amount: number) => {
    if (!amount) {
        return '0';
    }
    const formattedAmount = new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    }).format(amount);

    return `${formattedAmount}`;
};

export const hasAccessToPath = (menus: any, path: string): MenuType | null => {
    if (!menus) return null;
    for (const menu of menus) {
        if (menu.url === path) {
            return menu;
        }

        if (menu.sub_menus && menu.sub_menus.length > 0 && hasAccessToPath(menu.sub_menus, path)) {
            return menu.sub_menus.find((menu: any) => menu.url === path) || null;
        }
    }

    return null;
};

export const formatDuration = (m: number) => {
    return `${m >= 12 ? `${Math.floor(m / 12)} Year${m % 12 ? ' ' : ''}` : ''}${m % 12 ? `${m % 12} Month` : m < 12 ? `${m} Month` : ''}` || '0 Month';
}

export const Capitalize = (text?: string): string => {
    if (!text) return '--';
    return text
      .replace(/[_-]/g, ' ') // replaces both _ and -
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  

export const handleDownload = async (
    path: string,
    name: string,
    dispatch: any
) => {
    const payload = { path };
    const { code, data, message } = await DownloadFiles(payload);
    if (code === 200) {
        const extension = path?.split('.').pop()?.toLowerCase() || 'bin';
        const mimeMap: { [key: string]: string } = {
            pdf: 'application/pdf',
            png: 'image/png',
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            webp: 'image/webp',
            gif: 'image/gif',
            svg: 'image/svg+xml',
        };

        const mimeType = mimeMap[extension] || 'application/octet-stream';

        const blob = new Blob([data], { type: mimeType });
        const url = URL.createObjectURL(blob);

        const fileName = name.includes('.') ? name : `${name}.${extension}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } else {
        dispatch(
            showSnackbar({
                type: 'error',
                message: message || 'Something went wrong during file download',
            })
        );
    }
};

export const handleConvertToBlob = async (path: string, dispatch: any) => {
    const payload = { path };
    const { code, data } = await DownloadFiles(payload);

    if (code === 200) {
        const blob = new Blob([data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        return url;
    } else {
        dispatch(showSnackbar({ type: "error", message: 'Something Went Wrong' }));
    }
    return "";
};

export const handleConvertToBlobimage = async (path: string, dispatch: any) => {
    const payload = { path };
    const { code, data } = await DownloadFiles(payload);

    if (code === 200) {
        const blob = new Blob([data], { type: 'image/jpeg' }); // 👈 Update type based on your image type
        const url = window.URL.createObjectURL(blob);
        return url;
    } else {
        dispatch(showSnackbar({ type: "error", message: 'Something Went Wrong' }));
    }
    return "";
};



export const extractFilenameFromFilePath = (filePath: string | null | undefined): string => {
    if (!filePath) return "";
    const parts = filePath.split("/");
    return parts[parts.length - 1] || "";
};

export const getFileDetails = (filePath: string) => {
    const fullName = filePath.split('/').pop() || filePath; // gets last part after "/"
    const parts = fullName.split('.');
    const extension = parts.length > 1 ? parts.pop()!.toLowerCase() : '';
    const name = parts.join('.');
    return { name, extension };
};

export const numberToIndianWords = (num: number) => {
    const ones = [
        "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
        "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
        "Seventeen", "Eighteen", "Nineteen"
    ];

    const tens = [
        "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
    ];

    const units = [
        "", "Thousand", "Lakh", "Crore", "Arab", "Kharab", "Neel", "Padma", "Shankh", "Mahashankh"
    ];

    const convert = (num: number) => {
        if (num === 0) return "";
        let result = "";

        if (num >= 100) {
            result += ones[Math.floor(num / 100)] + " Hundred ";
            num %= 100;
        }

        if (num >= 20) {
            result += tens[Math.floor(num / 10)] + " ";
            num %= 10;
        }

        if (num > 0) {
            result += ones[num] + " ";
        }

        return result.trim();
    };

    if (num === 0) return "Zero Rupees";
    if (num > 999999999999999999) {
        return "Number exceeds 18 digits";
    }

    let result = "";
    let unitIndex = 0;
    if (num % 1000 > 0) {
        result = convert(num % 1000) + " " + units[unitIndex] + " ";
    }
    num = Math.floor(num / 1000);
    unitIndex++;
    while (num > 0 && unitIndex < units.length) {
        if (num % 100 > 0) {
            result = convert(num % 100) + " " + units[unitIndex] + " " + result;
        }
        num = Math.floor(num / 100);
        unitIndex++;
    }

    return result.trim() + " Rupees";
};

export function exportToExcel(data: any[], fileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);

    const workbook: XLSX.WorkBook = {
        Sheets: { 'Sheet1': worksheet },
        SheetNames: ['Sheet1']
    };
    const excelBuffer: any = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
    });
    const blob: Blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    FileSaver.saveAs(blob, `${fileName}.xlsx`);
}

export const handleFilePreview = (
    file: File | Blob | string | null,
    title: string,
    options: {
        setPreviewFile: (url: string) => void;
        setPreviewTitle: (title: string) => void;
        setOpenPreview: (open: boolean) => void;
    },
    dispatch: any
) => {

    const { setPreviewFile, setPreviewTitle, setOpenPreview } = options;


    if (!file) {
        return;
    }

    setPreviewTitle(title);

    if (file instanceof File || file instanceof Blob) {
        const fileUrl = URL.createObjectURL(file);
        if (file.type === "application/pdf") {
            window.open(fileUrl, "_blank");
        } else {
            setPreviewFile(fileUrl);
            setOpenPreview(true);
        }
        setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
    } else if (typeof file === "string") {
        dispatch(
            openDrawer({
                drawerData: file,
                name: "View Document",
                title: title,
                drawerWidth: "sm",
            })
        );
    } else {
        console.error("Unsupported file type:", file);
    }
};

export function filterNavigableMenus(menuData: (Menus | null | undefined)[]): Menus[] {
    if (!Array.isArray(menuData)) return [];

    return menuData.reduce<Menus[]>((acc, item) => {
        if (
            !item ||
            item.is_navigation !== 1 ||
            !Array.isArray(item.permissions) ||
            item.permissions.length === 0
        ) {
            return acc;
        }

        let validSubMenus: Menus[] | null = null;

        if (Array.isArray(item.sub_menus)) {
            for (const sub of item.sub_menus) {
                if (
                    sub &&
                    sub.is_navigation === 1 &&
                    Array.isArray(sub.permissions) &&
                    sub.permissions.length > 0
                ) {
                    validSubMenus ||= []; // lazy init
                    validSubMenus.push(sub);
                }
            }
        }

        acc.push(
            validSubMenus
                ? { ...item, sub_menus: validSubMenus }
                : { ...item, sub_menus: [] }
        );

        return acc;
    }, []);

    
}